import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const talentsCache = new Map()

export function useHeroData() {
  const route = useRoute()
  const router = useRouter()
  
  const hero = ref({})
  // Mantém os talentos de ambos os tipos separadamente
  const vanillaTalentsData = ref({})
  const modifiedTalentsData = ref({})
  const talentType = ref('modified')
  const selectedTalents = ref({
    modified: { 1: null, 4: null, 7: null, 10: null, 13: null, 16: null, 20: null },
    vanilla: { 1: null, 4: null, 7: null, 10: null, 13: null, 16: null, 20: null }
  })
  
  // Support both URL param and direct selection
  const selectedHeroName = ref('')
  
  const heroName = computed(() => selectedHeroName.value || route.params.name?.toLowerCase() || '')
  
  // Retorna os talentos do tipo atual (para compatibilidade com o código existente)
  const talents = computed(() => {
    return talentType.value === 'modified' ? modifiedTalentsData.value : vanillaTalentsData.value
  })
  
  // Mapeamento de nomes de heróis com hífen para nomes de arquivos sem hífen
  const heroNameToFileMap = {
    'lt-morales': 'lt-morales',
    'the-butcher': 'the-butcher',
    'the-lost-vikings': 'the-lost-vikings',
    'cho': 'cho',
    'gall': 'gall'
  }
  
  const heroPortraitPath = computed(() => {
    if (!heroName.value) return ''
    const fileName = heroNameToFileMap[heroName.value] || heroName.value
    // Usa caminho direto para pasta public (não passa pelo webpack)
    return `heroes_portraits/${fileName}.png`
  })
  
  // Helper para gerar caminho de imagem de talento (usa pasta public)
  const getTalentImagePath = (hero, imageName) => {
    if (!hero || !imageName) return ''
    // Se já for um caminho completo (começa com / ou http), retorna como está
    if (imageName.startsWith('/') || imageName.startsWith('http')) {
      return imageName
    }
    // Mapeia nomes de heróis com hífen para o formato da pasta
    const mappedHero = heroNameToFileMap[hero] || hero
    return `talents/${mappedHero}/${imageName}`
  }
  
  const heroSplashPath = computed(() => {
    if (!heroName.value) return ''
    try {
      return require(`@/assets/heroes_splash/${heroName.value}.webp`)
    } catch {
      try {
        return require(`@/assets/heroes_splash/${heroName.value}.jpg`)
      } catch {
        return ''
      }
    }
  })
  
  const currentSelectedTalents = computed(() => 
    selectedTalents.value[talentType.value]
  )
  
  const loadHeroData = (heroNameOverride = null) => {
    const targetHero = heroNameOverride || heroName.value
    if (!targetHero) return
    
    try {
      const heroData = require(`@/data/heroes/${targetHero}.json`)
      if (heroData) {
        hero.value = heroData
        // Carrega os talentos de AMBOS os tipos
        loadAllTalents(targetHero)
      }
    } catch (error) {
      console.error('Error loading hero data:', error)
    }
  }
  
  // Merge talentos: quando talentChanged é false, usa dados do vanilla
  const mergeTalentsWithVanilla = (modifiedData, vanillaData) => {
    if (!modifiedData || !vanillaData) {
      console.log('[mergeTalents] Dados insuficientes para merge:', { 
        hasModified: !!modifiedData, 
        hasVanilla: !!vanillaData 
      })
      return modifiedData
    }
    
    const merged = JSON.parse(JSON.stringify(modifiedData))
    let mergeCount = 0
    
    // Níveis de talentos
    const talentLevels = ['1', '4', '7', '10', '13', '16', '20']
    
    talentLevels.forEach(level => {
      if (!merged[level] || !vanillaData[level]) return
      
      merged[level] = merged[level].map((modifiedTalent) => {
        // Se o talento foi modificado, mantém os dados do modified
        if (modifiedTalent.talentChanged === true) {
          return modifiedTalent
        }
        
        // Procura o talento equivalente no vanilla pelo nome
        const vanillaTalent = vanillaData[level].find(vt => vt.name === modifiedTalent.name)
        
        if (vanillaTalent) {
          mergeCount++
          // Merge: mantém propriedades do modified mas substitui descrição e stats do vanilla
          return {
            ...modifiedTalent,
            description: vanillaTalent.description,
            // Mantém a imagem do modified se especificada, senão usa do vanilla
            image: modifiedTalent.image || vanillaTalent.image,
            // Atualiza cooldown e manaCost do vanilla se não foram explicitamente modificados
            cooldown: vanillaTalent.cooldown !== undefined ? vanillaTalent.cooldown : modifiedTalent.cooldown,
            manaCost: vanillaTalent.manaCost !== undefined ? vanillaTalent.manaCost : modifiedTalent.manaCost,
            // Se o vanilla tem passives/quest/rewards e o modified não tem, herda do vanilla
            passives: modifiedTalent.passives || vanillaTalent.passives,
            quest: modifiedTalent.quest || vanillaTalent.quest,
            rewards: modifiedTalent.rewards || vanillaTalent.rewards
          }
        }
        
        return modifiedTalent
      })
    })
    
    console.log(`[mergeTalents] Merge completo: ${mergeCount} talentos atualizados do vanilla`)
    
    // Também faz merge das abilities se não foram modificadas
    if (merged.abilities && vanillaData.abilities) {
      // Basic abilities
      if (merged.abilities.basic && vanillaData.abilities.basic) {
        merged.abilities.basic = merged.abilities.basic.map((modAbility, index) => {
          if (modAbility.abilityChanged === true) return modAbility
          const vanAbility = vanillaData.abilities.basic[index]
          if (!vanAbility) return modAbility
          return {
            ...modAbility,
            description: vanAbility.description,
            cooldown: vanAbility.cooldown !== undefined ? vanAbility.cooldown : modAbility.cooldown,
            manaCost: vanAbility.manaCost !== undefined ? vanAbility.manaCost : modAbility.manaCost
          }
        })
      }
      
      // Heroic abilities
      if (merged.abilities.heroic && vanillaData.abilities.heroic) {
        merged.abilities.heroic = merged.abilities.heroic.map((modAbility, index) => {
          if (modAbility.abilityChanged === true) return modAbility
          const vanAbility = vanillaData.abilities.heroic[index]
          if (!vanAbility) return modAbility
          return {
            ...modAbility,
            description: vanAbility.description,
            cooldown: vanAbility.cooldown !== undefined ? vanAbility.cooldown : modAbility.cooldown,
            manaCost: vanAbility.manaCost !== undefined ? vanAbility.manaCost : modAbility.manaCost
          }
        })
      }
      
      // Trait
      if (merged.abilities.trait && vanillaData.abilities.trait && 
          merged.abilities.trait.abilityChanged !== true) {
        merged.abilities.trait = {
          ...merged.abilities.trait,
          description: vanillaData.abilities.trait.description,
          cooldown: vanillaData.abilities.trait.cooldown !== undefined 
            ? vanillaData.abilities.trait.cooldown 
            : merged.abilities.trait.cooldown
        }
      }
    }
    
    return merged
  }

  // Carrega os talentos de um tipo específico
  const loadTalents = (targetHero = null, type = null) => {
    const hero = targetHero || heroName.value
    const targetType = type || talentType.value
    if (!hero) return
    
    const cacheKey = `${hero}-${targetType}`
    
    if (talentsCache.has(cacheKey)) {
      if (targetType === 'modified') {
        modifiedTalentsData.value = talentsCache.get(cacheKey)
      } else {
        vanillaTalentsData.value = talentsCache.get(cacheKey)
      }
      return
    }
    
    try {
      const fileName = targetType === 'modified'
        ? `${hero}_talents.json`
        : `${hero}_talents_vanilla.json`
      const data = require(`@/data/heroes/talents/${fileName}`)
      
      if (targetType === 'modified') {
        // Se está carregando modified, precisa primeiro garantir que o vanilla está carregado
        // para fazer o merge
        let vanillaData = vanillaTalentsData.value
        const hasVanillaData = vanillaData && vanillaData.abilities && vanillaData.abilities.basic && vanillaData.abilities.basic.length > 0
        
        if (!hasVanillaData) {
          try {
            vanillaData = require(`@/data/heroes/talents/${hero}_talents_vanilla.json`)
            vanillaTalentsData.value = vanillaData
            talentsCache.set(`${hero}-vanilla`, vanillaData)
            console.log(`[loadTalents] Vanilla carregado para merge: ${hero}`)
          } catch (err) {
            console.warn(`[loadTalents] Não foi possível carregar vanilla para ${hero}:`, err.message)
            vanillaData = null
          }
        } else {
          console.log(`[loadTalents] Vanilla já disponível em memory para: ${hero}`)
        }
        
        // Faz o merge dos talentos
        const mergedData = vanillaData ? mergeTalentsWithVanilla(data, vanillaData) : data
        modifiedTalentsData.value = mergedData
        talentsCache.set(cacheKey, mergedData)
        console.log(`[loadTalents] Modified merged e cacheado: ${hero}`)
      } else {
        vanillaTalentsData.value = data
        talentsCache.set(cacheKey, data)
      }
    } catch (error) {
      console.error(`Error loading ${targetType} talents:`, error)
      if (targetType === 'modified') {
        modifiedTalentsData.value = { abilities: { basic: [], heroic: [], trait: null, general: null } }
      } else {
        vanillaTalentsData.value = { abilities: { basic: [], heroic: [], trait: null, general: null } }
      }
    }
  }
  
  // Carrega os talentos de ambos os tipos
  const loadAllTalents = (targetHero = null) => {
    const hero = targetHero || heroName.value
    if (!hero) return
    
    loadTalents(hero, 'modified')
    loadTalents(hero, 'vanilla')
  }
  
  const selectHero = (heroName) => {
    selectedHeroName.value = heroName.toLowerCase()
    
    // Update URL without navigation (optional - for bookmarking)
    if (router) {
      router.replace({ path: `/hero/${heroName.toLowerCase()}`, query: route.query })
        .catch(() => {}) // Ignore navigation errors
    }
    
    // Reset selections when changing hero
    resetSelections()
    
    // Load new hero data
    loadHeroData(heroName.toLowerCase())
  }
  
  const toggleTalentType = () => {
    talentType.value = talentType.value === 'modified' ? 'vanilla' : 'modified'
    // Não precisa carregar talentos aqui pois já temos ambos carregados
  }
  
  const setTalentType = (type) => {
    if (type === 'modified' || type === 'vanilla') {
      talentType.value = type
      // Não precisa carregar talentos aqui pois já temos ambos carregados
    }
  }
  
  const toggleTalentSelection = (level, talent) => {
    const type = talentType.value
    const current = selectedTalents.value[type]
    // Comparar por nome em vez de referência de objeto
    const isCurrentlySelected = current[level]?.name === talent.name
    const newValue = isCurrentlySelected ? null : talent
    
    // Criar novo objeto para garantir reatividade
    selectedTalents.value = {
      ...selectedTalents.value,
      [type]: {
        ...current,
        [level]: newValue
      }
    }
  }
  
  const resetSelections = () => {
    selectedTalents.value = {
      modified: { 1: null, 4: null, 7: null, 10: null, 13: null, 16: null, 20: null },
      vanilla: { 1: null, 4: null, 7: null, 10: null, 13: null, 16: null, 20: null }
    }
  }
  
  const isSelected = (level, talent) => 
    currentSelectedTalents.value[level]?.name === talent.name
  
  const isAnySelected = (level) => 
    currentSelectedTalents.value[level] !== null && currentSelectedTalents.value[level] !== undefined
  
  const findAbilityOrTalent = (type, section, category, index, targetHero) => {
    const hero = targetHero || heroName.value
    if (!hero) return null
    
    const cacheKey = `${hero}-${type}`
    
    let talentsData
    if (talentsCache.has(cacheKey)) {
      talentsData = talentsCache.get(cacheKey)
    } else {
      try {
        const fileName = type === 'modified'
          ? `${hero}_talents.json`
          : `${hero}_talents_vanilla.json`
        talentsData = require(`@/data/heroes/talents/${fileName}`)
        talentsCache.set(cacheKey, talentsData)
      } catch {
        return null
      }
    }
    
    if (section === 'abilities') {
      if (category === 'trait') return talentsData.abilities.trait
      if (category === 'basic') return talentsData.abilities.basic?.[index]
      if (category === 'heroic') return talentsData.abilities.heroic?.[index]
      if (category === 'general') return talentsData.abilities.general
    } else if (section === 'talents') {
      return talentsData[category]?.[index]
    }
    return null
  }
  
  // Initialize from URL if present
  if (route.params.name) {
    selectedHeroName.value = route.params.name.toLowerCase()
    loadHeroData()
  }
  
  // Watch for URL changes
  watch(() => route.params.name, (newName) => {
    if (newName && newName.toLowerCase() !== selectedHeroName.value) {
      selectedHeroName.value = newName.toLowerCase()
      loadHeroData()
    }
  })
  
  const setSelectedTalents = (type, selections) => {
    if (type === 'modified' || type === 'vanilla') {
      // Cria uma nova referência para garantir reatividade
      selectedTalents.value = {
        ...selectedTalents.value,
        [type]: { ...selectedTalents.value[type], ...selections }
      }
    }
  }

  return {
    hero,
    talents,
    vanillaTalentsData,
    modifiedTalentsData,
    heroName,
    heroPortraitPath,
    heroSplashPath,
    talentType,
    selectedTalents,
    currentSelectedTalents,
    selectedHeroName,
    loadHeroData,
    loadTalents,
    loadAllTalents,
    selectHero,
    toggleTalentType,
    setTalentType,
    toggleTalentSelection,
    resetSelections,
    setSelectedTalents,
    isSelected,
    isAnySelected,
    findAbilityOrTalent,
    getTalentImagePath
  }
}
